/**
 * Copilot "reduced mode" — Phase 0 telemetry surfaced in the UI (Phase 6).
 *
 * Backend emits SSE ``type: "degraded"`` and/or ``complete.data.degrade_reason``
 * when the turn fell back from the LLM path (circuit open, timeout, heuristic
 * planner/binder, etc.). History reload reads the same signal from Laravel's
 * message resource (``degraded`` / ``degrade_reason`` / ``service_path``).
 */

const HEURISTIC_SERVICE_PATHS = new Set([
  "heuristic_planner",
  "heuristic_binder",
  "heuristic_both",
]);

/**
 * @param {string|null|undefined} degradeReason
 * @param {string|null|undefined} servicePath
 * @returns {boolean}
 */
export function isCopilotReducedMode(degradeReason, servicePath) {
  const reason = String(degradeReason || "")
    .trim()
    .toLowerCase();
  if (reason && reason !== "none") {
    return true;
  }
  const path = String(servicePath || "")
    .trim()
    .toLowerCase();
  return HEURISTIC_SERVICE_PATHS.has(path);
}

/**
 * Normalize telemetry from SSE events or Laravel message fields.
 * @param {object|null|undefined} input
 * @returns {{ isReducedMode: boolean, degradeReason: string|null, servicePath: string|null }}
 */
export function normalizeCopilotDegradedPayload(input) {
  if (!input || typeof input !== "object") {
    return { isReducedMode: false, degradeReason: null, servicePath: null };
  }

  const degradeReasonRaw =
    input.degrade_reason ?? input.degradeReason ?? null;
  const servicePathRaw = input.service_path ?? input.servicePath ?? null;
  const degradeReason =
    typeof degradeReasonRaw === "string" && degradeReasonRaw.trim()
      ? degradeReasonRaw.trim()
      : null;
  const servicePath =
    typeof servicePathRaw === "string" && servicePathRaw.trim()
      ? servicePathRaw.trim()
      : null;

  const flagged =
    input.degraded === true ||
    input.isReducedMode === true ||
    isCopilotReducedMode(degradeReason, servicePath);

  if (!flagged) {
    return { isReducedMode: false, degradeReason: null, servicePath: null };
  }

  return {
    isReducedMode: true,
    degradeReason:
      degradeReason && degradeReason.toLowerCase() !== "none"
        ? degradeReason
        : null,
    servicePath,
  };
}

/**
 * Short user-facing copy. Internal reason codes stay in ``title`` only.
 * @param {string|null|undefined} degradeReason
 * @param {string|null|undefined} servicePath
 * @returns {{ label: string, detail: string }}
 */
export function getCopilotReducedModeCopy(degradeReason, servicePath) {
  const reason = String(degradeReason || "")
    .trim()
    .toLowerCase();
  const path = String(servicePath || "")
    .trim()
    .toLowerCase();

  let detail =
    "Answer quality may be lower than usual. You can retry in a moment.";

  if (
    reason === "circuit_open" ||
    reason === "capacity_timeout" ||
    reason === "budget_exhausted" ||
    reason === "no_api_key"
  ) {
    detail =
      "AI assist was temporarily unavailable, so a simpler match was used.";
  } else if (
    reason === "llm_timeout" ||
    reason === "retries_exhausted" ||
    reason === "llm_exception" ||
    reason === "llm_empty" ||
    reason === "llm_parse_failed"
  ) {
    detail =
      "The AI model did not respond in time, so a simpler match was used.";
  } else if (
    HEURISTIC_SERVICE_PATHS.has(path) ||
    reason === "prompt_missing"
  ) {
    detail = "Answered with keyword matching while AI assist was limited.";
  }

  return {
    label: "Answered in reduced mode",
    detail,
  };
}
